/**
 * Authentication token management utility
 * Handles token storage in both localStorage and cookies for cross-subdomain access
 */

const TOKEN_KEY = 'token';
const COOKIE_NAME = 'auth_token';

/**
 * Get the cookie domain for cross-subdomain access
 * Returns the root domain (e.g., .jadwalh.com) to allow sharing across subdomains
 */
const getCookieDomain = (): string => {
  const hostname = window.location.hostname;

  // For localhost development, return hostname as-is
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname;
  }

  // Extract root domain (e.g., example.com from app.example.com)
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }

  return hostname;
};

/**
 * Set a cookie with the specified name, value, and options
 */
const setCookie = (name: string, value: string, days: number = 365): void => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  const domain = getCookieDomain();
  const secure = window.location.protocol === 'https:' ? 'Secure;' : '';

  document.cookie = `${name}=${value};${expires};domain=${domain};path=/;${secure}SameSite=Lax`;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
};

/**
 * Delete a cookie by name
 */
const deleteCookie = (name: string): void => {
  const domain = getCookieDomain();
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;domain=${domain};path=/;`;
};

/**
 * Set authentication token in both localStorage and cookie
 * @param token - The authentication token to store
 */
export const setAuthToken = (token: string | null | undefined): void => {
  // Only set token if it's a valid string
  if (!token || token === 'null' || token === 'undefined') {
    return;
  }

  // Set in localStorage for backward compatibility
  localStorage.setItem(TOKEN_KEY, token);

  // Set in cookie for cross-subdomain access
  setCookie(COOKIE_NAME, token);
};

/**
 * Get authentication token from localStorage
 * (Cookie is primarily used by user app, localStorage by client app)
 * @returns The stored token or null if not found
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove authentication token from both localStorage and cookie
 */
export const removeAuthToken = (): void => {
  // Remove from localStorage
  localStorage.removeItem(TOKEN_KEY);

  // Remove cookie
  deleteCookie(COOKIE_NAME);
};

/**
 * Check if user is authenticated (has a valid token)
 * @returns true if token exists, false otherwise
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return !!token && token !== '';
};
